import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Loader2, Download, LayoutTemplate, Trash2, Sparkles, Upload, X, Maximize2, Minimize2 } from "lucide-react";

const MODELS = [
  { id: "nano-banana", label: "Nano Banana", hint: "Fast · great everyday quality" },
  { id: "nano-banana-pro", label: "Nano Banana Pro", hint: "Highest quality · 4K output" },
];
const ASPECTS = [
  { id: "square", label: "Square", ratio: "1:1", w: 1080, h: 1080 },
  { id: "portrait", label: "Portrait", ratio: "4:5", w: 1080, h: 1350 },
  { id: "story", label: "Story", ratio: "9:16", w: 1080, h: 1920 },
  { id: "landscape", label: "Landscape", ratio: "16:9", w: 1600, h: 900 },
];
const STYLES = ["photorealistic", "3D render", "minimal vector", "watercolor", "cinematic", "gold luxury"];
const CHECKER = {
  backgroundColor: "#fff",
  backgroundImage: "linear-gradient(45deg,#dcdcdc 25%,transparent 25%),linear-gradient(-45deg,#dcdcdc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#dcdcdc 75%),linear-gradient(-45deg,transparent 75%,#dcdcdc 75%)",
  backgroundSize: "18px 18px",
  backgroundPosition: "0 0,0 9px,9px -9px,-9px 0",
};

export default function ImageStudio() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("nano-banana-pro");
  const [aspect, setAspect] = useState("square");
  const [transparent, setTransparent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [refImage, setRefImage] = useState(null);
  const fileRef = useRef(null);
  const [viewer, setViewer] = useState(null);
  const [actualSize, setActualSize] = useState(false);

  const modelLabel = (id) => MODELS.find((m) => m.id === id)?.label || id;
  const openViewer = (item) => { setActualSize(false); setViewer(item); };

  const loadGallery = () => api.get("/ai/images").then((r) => setGallery(r.data)).catch(() => {});
  useEffect(() => { loadGallery(); }, []);

  const addStyle = (s) => setPrompt((p) => (p ? `${p}, ${s}` : s));

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setRefImage(reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image you want"); return; }
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post("/ai/image", { prompt, model, aspect, image: refImage, count: 2, transparent });
      const imgs = data.images || [data];
      setResults(imgs);
      setGallery((g) => [...imgs, ...g]);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const download = (src, name = "kann-ai-image") => {
    const a = document.createElement("a");
    a.href = src; a.download = `${name}.png`; a.click();
  };

  const sendToDesign = async (item) => {
    const a = ASPECTS.find((x) => x.id === item.aspect) || ASPECTS[0];
    try {
      const { data } = await api.post("/designs", {
        name: "AI artwork", width: a.w, height: a.h, background: "#FFFFFF",
        elements: [{ id: "ai-img", type: "image", src: item.image, x: 0, y: 0, width: a.w, height: a.h, objectFit: "cover", borderRadius: 0, opacity: 1 }],
      });
      navigate(`/editor/${data.id}`);
    } catch (e) { toast.error("Could not create design"); }
  };

  const remove = async (id) => {
    await api.delete(`/ai/images/${id}`);
    setGallery((g) => g.filter((x) => x.id !== id));
    setResults((r) => (r ? r.filter((x) => x.id !== id) : r));
    toast.success("Deleted");
  };

  const currentAspect = ASPECTS.find((a) => a.id === aspect) || ASPECTS[0];

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-16 md:pt-10 relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 620, height: 620, top: -140, left: "55%" }} /></div>

        <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">Powered by Google Nano Banana</div>
        <h1 className="text-3xl sm:text-4xl font-medium text-kann-text-pri mt-2">AI Image Studio</h1>
        <p className="font-light text-kann-text-sec mt-2">Generate 2 original artwork variations from a prompt — then drop your favourite straight into a design.</p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 space-y-5">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Model</label>
              <div className="mt-3 grid grid-cols-2 gap-2" data-testid="model-selector">
                {MODELS.map((m) => (
                  <button key={m.id} onClick={() => setModel(m.id)} data-testid={`model-${m.id}`}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${model === m.id ? "border-kann-gold bg-kann-gold/10" : "border-kann-border/40 hover:bg-kann-surface-active"}`}>
                    <div className="text-sm font-medium text-kann-text-pri flex items-center gap-1.5">{m.label}{m.id === "nano-banana-pro" && <Sparkles className="w-3.5 h-3.5 text-kann-gold" />}</div>
                    <div className="text-[11px] text-kann-text-sec">{m.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Aspect ratio</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {ASPECTS.map((a) => (
                  <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`aspect-${a.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${aspect === a.id ? "bg-kann-ink text-white font-semibold" : "border border-kann-border/40 text-kann-text-sec hover:bg-kann-surface-active"}`}>
                    {a.label} <span className="opacity-60">{a.ratio}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Background</label>
              <button onClick={() => setTransparent((v) => !v)} data-testid="transparent-toggle"
                className={`mt-3 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${transparent ? "border-kann-gold bg-kann-gold/10" : "border-kann-border/40 hover:bg-kann-surface-active"}`}>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 shrink-0 transition-colors ${transparent ? "bg-kann-gold justify-end" : "bg-kann-border/50 justify-start"}`}>
                  <span className="w-4 h-4 rounded-full bg-white shadow" />
                </span>
                <span className="text-left">
                  <span className="block text-sm font-medium text-kann-text-pri">Transparent background</span>
                  <span className="block text-[11px] text-kann-text-sec">Real cut-out PNG (green-screen keyed) with a soft drop shadow added — works for logos, products & white objects</span>
                </span>
              </button>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Prompt</label>
              <textarea data-testid="image-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
                placeholder="e.g. a floating golden sneaker on a dark studio backdrop, dramatic lighting"
                className="mt-3 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2.5 text-sm outline-none resize-none" />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {STYLES.map((s) => (
                  <button key={s} onClick={() => addStyle(s)} className="text-[11px] px-2.5 py-1 rounded-full border border-kann-border/40 text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors">+ {s}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Reference image (optional)</label>
              {refImage ? (
                <div className="mt-3 flex items-center gap-3">
                  <img src={refImage} alt="reference" className="w-16 h-16 rounded-md object-cover border border-kann-border/40" data-testid="reference-thumb" />
                  <div className="text-xs text-kann-text-sec">Nano Banana will build on this image.</div>
                  <button onClick={() => setRefImage(null)} data-testid="remove-reference-btn" className="ml-auto p-1.5 rounded-md hover:bg-kann-surface-active text-kann-text-sec"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} data-testid="upload-reference-btn"
                  className="mt-3 w-full border border-dashed border-kann-border/50 rounded-md py-3 text-sm text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" /> Upload an image to build on
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="reference-file-input" />
            </div>

            <button onClick={generate} disabled={busy} data-testid="generate-image-btn"
              className="w-full bg-kann-ink text-white font-semibold rounded-full py-3 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {busy ? "Generating…" : "Generate image"}
            </button>
          </div>

          {/* Preview */}
          <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Preview {results && results.length > 1 ? `· ${results.length} variations` : ""}</span>
              {results?.[0]?.resolution === "4K" && <span data-testid="resolution-badge" className="text-[10px] font-bold tracking-wide bg-kann-gold text-kann-ink px-2 py-0.5 rounded-full">4K</span>}
            </div>
            <div className="flex-1 flex items-center justify-center bg-kann-base rounded-md border border-kann-border/30 overflow-hidden p-2" style={{ minHeight: 300 }} data-testid="image-preview">
              {busy ? (
                <div className="text-center text-kann-text-sec"><Loader2 className="w-7 h-7 animate-spin mx-auto mb-2 text-kann-gold" />Creating 2 variations…</div>
              ) : results && results.length ? (
                <div className={`grid ${results.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2 w-full`} data-testid="latest-results">
                  {results.map((item, i) => (
                    <div key={item.id} className="group relative rounded-md overflow-hidden border border-kann-border/30 flex items-center justify-center" style={item.transparent ? CHECKER : { background: "#f4f4f4" }} data-testid={`latest-image-${i}`}>
                      <img src={item.image} alt={item.prompt} onClick={() => openViewer(item)} className="max-w-full max-h-[360px] object-contain cursor-zoom-in" title="Click to view full resolution" />
                      <div className="absolute inset-x-0 bottom-0 flex gap-1.5 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => sendToDesign(item)} data-testid={`use-result-${i}`} className="flex-1 bg-kann-gold text-kann-ink text-xs font-semibold rounded-full py-1.5 hover:bg-yellow-500 flex items-center justify-center gap-1"><LayoutTemplate className="w-3.5 h-3.5" /> Use</button>
                        <button onClick={() => download(item.image)} data-testid={`download-result-${i}`} className="border border-white/50 text-white text-xs rounded-full px-3 py-1.5 hover:bg-white/10 flex items-center gap-1"><Download className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-kann-text-sec text-sm px-6 text-center">Your 2 generated variations will appear here ({currentAspect.label} {currentAspect.ratio}).</span>
              )}
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-5">
            <Wand2 className="w-4 h-4 text-kann-gold" />
            <h2 className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Your generations</h2>
          </div>
          {gallery.length === 0 ? (
            <div className="border border-dashed border-kann-border/40 rounded-lg py-16 text-center text-kann-text-sec">No images yet — generate your first above.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="image-gallery">
              {gallery.map((item) => (
                <div key={item.id} onClick={() => openViewer(item)} data-testid={`gallery-item-${item.id}`}
                  className="group relative bg-kann-surface border border-kann-border/30 rounded-lg overflow-hidden cursor-zoom-in">
                  {item.resolution === "4K" && <span className="absolute top-2 left-2 z-10 text-[10px] font-bold bg-kann-gold text-kann-ink px-1.5 py-0.5 rounded-full">4K</span>}
                  <div className="aspect-square flex items-center justify-center overflow-hidden" style={item.transparent ? CHECKER : { background: "#f4f4f4" }}>
                    <img src={item.image} alt={item.prompt} className={`w-full h-full ${item.transparent ? "object-contain" : "object-cover"}`} />
                  </div>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                    <button onClick={(e) => { e.stopPropagation(); sendToDesign(item); }} className="w-full bg-kann-gold text-kann-ink text-xs font-semibold rounded-full py-2 hover:bg-yellow-500">Use in design</button>
                    <div className="flex gap-2 w-full">
                      <button onClick={(e) => { e.stopPropagation(); download(item.image); }} className="flex-1 border border-white/40 text-white text-xs rounded-full py-2 hover:bg-white/10">Download</button>
                      <button onClick={(e) => { e.stopPropagation(); remove(item.id); }} data-testid={`delete-image-${item.id}`} className="border border-white/40 text-white rounded-full p-2 hover:bg-red-500/30"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {viewer && (
          <div className="fixed inset-0 z-[1000] bg-black/90 flex flex-col" data-testid="image-viewer" onClick={() => setViewer(null)}>
            <div className="flex items-center gap-2 p-3 md:p-4 flex-wrap" onClick={(e) => e.stopPropagation()}>
              <div className="min-w-0 mr-auto">
                <div className="text-sm text-white truncate max-w-[60vw] md:max-w-[46vw]">{viewer.prompt}</div>
                <div className="text-xs text-white/60">{modelLabel(viewer.model)} · {viewer.aspect} · {viewer.resolution === "4K" ? "4K" : "standard"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setActualSize((v) => !v)} data-testid="viewer-actualsize" className="border border-white/40 text-white text-sm rounded-full px-3 md:px-4 py-2 hover:bg-white/10 flex items-center gap-2">{actualSize ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />} <span className="hidden sm:inline">{actualSize ? "Fit" : "Actual size"}</span></button>
                <button onClick={() => sendToDesign(viewer)} data-testid="viewer-use" className="bg-kann-gold text-kann-ink text-sm font-semibold rounded-full px-3 md:px-4 py-2 hover:bg-yellow-500 flex items-center gap-2"><LayoutTemplate className="w-4 h-4" /> <span className="hidden sm:inline">Use in design</span></button>
                <button onClick={() => download(viewer.image)} data-testid="viewer-download" className="border border-white/40 text-white text-sm rounded-full px-3 md:px-4 py-2 hover:bg-white/10 flex items-center gap-2"><Download className="w-4 h-4" /> <span className="hidden sm:inline">Download</span></button>
                <button onClick={() => setViewer(null)} data-testid="viewer-close" className="p-2 rounded-full hover:bg-white/10 text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center p-4" onClick={() => setViewer(null)}>
              <img src={viewer.image} alt={viewer.prompt} data-testid="viewer-image" onClick={(e) => { e.stopPropagation(); setActualSize((v) => !v); }}
                style={viewer.transparent ? CHECKER : undefined}
                className={actualSize ? "max-w-none cursor-zoom-out" : "max-h-[82vh] max-w-[92vw] object-contain cursor-zoom-out"} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
